// Client-side dashboard -> PDF export. Runs entirely in the browser: it
// snapshots the already-rendered Lecture Mode DOM (the same read-only grid
// used for sharing) and slices it across A4 pages. No backend involved —
// deliberately, since the widgets render their charts (ApexCharts SVG) with
// JavaScript in the browser; a server-side renderer would need either a full
// headless browser (heavy on the small EB instance this app runs on) or a
// from-scratch reimplementation of the chart rendering in Python. Both cost
// far more than they're worth for an export feature.
//
// Uses html-to-image (not html2canvas): react-grid-layout positions every
// widget with its own `transform: translate(x, y)`, and html2canvas's
// hand-rolled CSS/text layout engine mis-measures wrapped, centered text
// inside those narrow transformed containers — wrapped lines end up drawn
// on top of each other ("No content" and "available." overlapping). Its
// foreignObjectRendering mode fixes the text but renders solid black
// instead of the widgets. html-to-image clones the subtree and copies each
// node's actual *computed* styles onto the clone before serializing it to
// an SVG <foreignObject>, so it reproduces exactly what's on screen without
// either failure mode.
//
// html-to-image/jsPDF are loaded on demand so they don't add weight to the
// main bundle for users who never export.

const A4_WIDTH_MM = 210
const A4_HEIGHT_MM = 297
const PDF_MARGIN_MM = 8

export async function exportElementToPdf(element, filename) {
  if (!element) {
    throw new Error('Nothing to export: the dashboard is not rendered.')
  }

  const [{ toCanvas }, { jsPDF }] = await Promise.all([
    import('html-to-image'),
    import('jspdf'),
  ])

  // Wait for fonts so text metrics match what's on screen before snapshotting.
  if (document.fonts?.ready) {
    await document.fonts.ready
  }

  const canvas = await toCanvas(element, {
    pixelRatio: Math.min(2, window.devicePixelRatio > 1 ? 2 : 1.5),
    backgroundColor: '#ffffff',
    width: element.scrollWidth,
    height: element.scrollHeight,
  })

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const contentWidthMm = A4_WIDTH_MM - PDF_MARGIN_MM * 2
  const contentHeightMm = A4_HEIGHT_MM - PDF_MARGIN_MM * 2

  // How many source pixels (at canvas resolution) fit into one PDF page,
  // preserving the canvas's own aspect ratio.
  const pxPerMm = canvas.width / contentWidthMm
  const pageHeightPx = Math.floor(contentHeightMm * pxPerMm)

  let renderedPx = 0
  let isFirstPage = true

  while (renderedPx < canvas.height) {
    const sliceHeightPx = Math.min(pageHeightPx, canvas.height - renderedPx)

    const pageCanvas = document.createElement('canvas')
    pageCanvas.width = canvas.width
    pageCanvas.height = sliceHeightPx
    pageCanvas.getContext('2d').drawImage(
      canvas,
      0, renderedPx, canvas.width, sliceHeightPx,
      0, 0, canvas.width, sliceHeightPx,
    )

    const sliceHeightMm = sliceHeightPx / pxPerMm
    const imgData = pageCanvas.toDataURL('image/jpeg', 0.92)

    if (!isFirstPage) pdf.addPage()
    pdf.addImage(imgData, 'JPEG', PDF_MARGIN_MM, PDF_MARGIN_MM, contentWidthMm, sliceHeightMm)

    renderedPx += sliceHeightPx
    isFirstPage = false
  }

  pdf.save(filename)
}

export const slugifyFilename = (name) => (
  (name || 'dashboard').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'dashboard'
)
